import { useTranslation } from 'react-i18next'

import { Check, CheckCheck, CircleCheck, Clock, MessageCircle, Scale, TriangleAlert } from 'lucide-react'

import { cn } from '@/lib/utils'

type MockCardProps = {
    compact?: boolean
    /** Merged last, so callers can reposition or re-rotate the card. */
    className?: string
}

const CARD_SHADOW = 'shadow-[0_2px_10px_oklch(0%_0_0/0.25)]'
const FLOAT_SHADOW = 'shadow-[0_18px_40px_oklch(0%_0_0/0.5)]'
const ON_FLAME = 'text-[oklch(98%_0.01_60)]'
const CREAM_MUTED = 'text-[oklch(38%_0.04_48)]'
const DONUT_BG =
    'conic-gradient(var(--tipi-flame) 0 31%, var(--tipi-ember) 31% 65%, oklch(45% 0.05 48) 65% 75%, oklch(62% 0.04 60) 75% 81%, oklch(76% 0.03 80) 81% 88%, var(--tipi-green) 88% 100%)'

function Donut({ size, inset }: { size: number; inset: number }) {
    return (
        <div className='relative shrink-0 rounded-full' style={{ width: size, height: size, background: DONUT_BG }}>
            <div className='absolute rounded-full bg-tipi-cream' style={{ inset }} />
        </div>
    )
}

function LegendRow({ color, label, value, dot = 8 }: { color: string; label: string; value: string; dot?: number }) {
    return (
        <span className='flex items-center gap-1.5'>
            <span className='rounded-full' style={{ width: dot, height: dot, background: color }} />
            <span className='flex-1'>{label}</span>
            <span className='font-extrabold'>{value}</span>
        </span>
    )
}

function MarginBar({ greenPct }: { greenPct: number }) {
    return (
        <div className='relative h-1 rounded-full bg-[oklch(100%_0_0/0.08)]'>
            <div
                className='absolute left-0 top-0 h-full rounded-l-full bg-tipi-green'
                style={{ width: `${greenPct}%` }}
            />
            <div
                className='absolute top-0 h-full rounded-r-full bg-tipi-flame'
                style={{ left: `${greenPct}%`, width: `${100 - greenPct}%` }}
            />
        </div>
    )
}

export function PriceRiseCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'overflow-hidden bg-tipi-cream text-tipi-cream-fg',
                CARD_SHADOW,
                compact ? 'absolute left-0 top-0 w-[262px] -rotate-[5deg] rounded-3xl' : 'w-[300px] rounded-[28px]',
                className,
            )}
        >
            <div
                className={cn(
                    'flex items-start justify-between gap-2.5',
                    compact ? 'px-3.5 pb-2.5 pt-3.5' : 'p-4 pb-3',
                )}
            >
                <div className='flex min-w-0 flex-col gap-[3px]'>
                    <span
                        className={cn(
                            'font-bold uppercase tracking-[0.14em] text-[oklch(45%_0.11_40)]',
                            compact ? 'text-[10px]' : 'text-[11px]',
                        )}
                    >
                        {t('routerMocks.priceRise.label', 'Impacto de subidas')}
                    </span>
                    <span
                        className={cn(
                            'whitespace-nowrap font-extrabold tabular-nums leading-[0.98] tracking-[-0.035em]',
                            compact ? 'text-[30px]' : 'text-[34px]',
                        )}
                    >
                        {t('routerMocks.priceRise.amount', '1.284,50 €')}
                    </span>
                    <span className='text-[11px] font-semibold leading-snug text-[oklch(42%_0.045_48)]'>
                        {compact
                            ? t('routerMocks.priceRise.lostShort', 'perdidos por subidas')
                            : t('routerMocks.priceRise.lost', 'perdidos por subidas de precio')}
                    </span>
                </div>
                <span
                    className={cn(
                        'flex shrink-0 items-center gap-[5px] whitespace-nowrap rounded-full bg-tipi-flame px-2.5 py-1 text-[11px] font-bold',
                        ON_FLAME,
                    )}
                >
                    <TriangleAlert size={13} strokeWidth={2.2} />
                    {compact ? '3' : t('routerMocks.priceRise.rises', '3 subidas')}
                </span>
            </div>
            <div className='mx-4 h-px bg-[oklch(28%_0.045_48/0.16)]' />
            {compact ? (
                <div className='flex items-center justify-between gap-2 px-3.5 pb-12 pt-2.5 text-xs tabular-nums'>
                    <span className='font-bold'>{t('routerMocks.priceRise.ingredient', 'Mero amarillo')}</span>
                    <span className={cn('rounded-full bg-tipi-flame px-2 py-0.5 text-[10px] font-bold', ON_FLAME)}>
                        {t('routerMocks.priceRise.delta', '+5,50 €/kg')}
                    </span>
                </div>
            ) : (
                <div className='p-3'>
                    <div className='grid grid-cols-[1fr_auto] items-start gap-x-3 gap-y-2 rounded-2xl border border-[oklch(28%_0.045_48/0.14)] bg-[oklch(97%_0.018_88)] p-3'>
                        <div className='flex flex-col gap-1'>
                            <span className='text-sm font-bold tracking-[-0.01em]'>
                                {t('routerMocks.priceRise.ingredient', 'Mero amarillo')}
                            </span>
                            <span className='flex items-center gap-1 text-[11px] font-semibold text-[oklch(45%_0.045_48)]'>
                                <Clock size={12} />
                                {t('routerMocks.priceRise.ago', 'hace 3 días')}
                            </span>
                        </div>
                        <div className='flex flex-col items-end gap-[5px] text-xs tabular-nums'>
                            <span className='whitespace-nowrap'>
                                <span className='text-[oklch(48%_0.04_48)] line-through'>
                                    {t('routerMocks.priceRise.oldPrice', '23,00 €')}
                                </span>
                                <span className='mx-0.5 text-[oklch(48%_0.04_48)]'>→</span>
                                <span className='font-extrabold'>
                                    {t('routerMocks.priceRise.newPrice', '28,50 €/kg')}
                                </span>
                            </span>
                            <span
                                className={cn('rounded-full bg-tipi-flame px-2 py-0.5 text-[11px] font-bold', ON_FLAME)}
                            >
                                {t('routerMocks.priceRise.delta', '+5,50 €/kg')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export function WhatsAppCard({ compact, className, message }: MockCardProps & { message?: string }) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'overflow-hidden border border-tipi-border bg-tipi-surface',
                compact
                    ? cn('absolute bottom-0 right-0 w-[240px] rotate-[4deg] rounded-[18px]', FLOAT_SHADOW)
                    : cn('w-[290px] -translate-x-[60px] -rotate-3 rounded-[20px]', CARD_SHADOW),
                className,
            )}
        >
            <div
                className={cn('flex items-center bg-tipi-shell', compact ? 'gap-2 px-3 py-2' : 'gap-2.5 px-3.5 py-2.5')}
            >
                <span
                    className={cn(
                        'flex items-center justify-center rounded-full bg-tipi-surface-up font-extrabold',
                        compact ? 'size-[26px] text-[11px]' : 'size-8 text-[13px]',
                    )}
                >
                    M
                </span>
                <span className='flex flex-1 flex-col gap-px'>
                    <span className={cn('font-bold', compact ? 'text-xs' : 'text-[13px]')}>
                        {t('routerMocks.whatsApp.contact', 'Makro · Pescado')}
                    </span>
                    {compact ? null : <span className='text-[11px] text-tipi-muted'>WhatsApp</span>}
                </span>
                <MessageCircle size={compact ? 16 : 18} className='text-tipi-green' />
            </div>
            <div className={cn('flex flex-col items-end', compact ? 'p-2.5' : 'gap-1.5 p-3')}>
                {compact ? null : (
                    <span className='text-[10px] font-bold uppercase tracking-[0.14em] text-tipi-muted'>
                        {t('routerMocks.whatsApp.draftedBy', 'Redactado por Tipi')}
                    </span>
                )}
                <div
                    className={cn(
                        'flex flex-col gap-1 bg-[oklch(36%_0.06_160)]',
                        compact
                            ? 'rounded-xl rounded-br-[4px] px-2.5 pb-[5px] pt-2'
                            : 'max-w-[92%] rounded-[14px] rounded-br-[4px] px-3 pb-1.5 pt-2.5',
                    )}
                >
                    <span className={cn('leading-[1.4]', compact ? 'text-xs' : 'text-[13px]')}>
                        {message ??
                            (compact
                                ? t(
                                      'routerMocks.whatsApp.messageShort',
                                      'Hola, el mero ha pasado de 23,00 € a 28,50 €/kg. ¿Podemos revisar el precio?',
                                  )
                                : t(
                                      'routerMocks.whatsApp.message',
                                      'Hola, el mero ha pasado de 23,00 € a 28,50 €/kg en el último albarán. ¿Podemos revisar el precio?',
                                  ))}
                    </span>
                    <span
                        className={cn(
                            'flex items-center gap-[3px] self-end text-[oklch(85%_0.03_160)]',
                            compact ? 'text-[9px]' : 'text-[10px]',
                        )}
                    >
                        10:42
                        <CheckCheck size={compact ? 12 : 14} strokeWidth={2.2} />
                    </span>
                </div>
            </div>
        </div>
    )
}

export function ResultCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'flex flex-col bg-tipi-cream text-tipi-cream-fg',
                CARD_SHADOW,
                compact
                    ? 'absolute left-0 top-0 w-[256px] -rotate-[4deg] gap-2.5 rounded-3xl px-3.5 pb-10 pt-3.5'
                    : 'w-[300px] -translate-x-2.5 rotate-3 gap-3.5 rounded-[28px] p-[18px]',
                className,
            )}
        >
            <div className='flex flex-col gap-[3px]'>
                <span
                    className={cn(
                        'font-bold uppercase tracking-[0.14em]',
                        CREAM_MUTED,
                        compact ? 'text-[10px]' : 'text-[11px]',
                    )}
                >
                    {t('routerMocks.result.label', 'Tu resultado · Octubre')}
                </span>
                <span
                    className={cn(
                        'font-extrabold tabular-nums leading-[0.98] tracking-[-0.035em]',
                        compact ? 'text-[30px]' : 'text-[34px]',
                    )}
                >
                    {t('routerMocks.result.amount', '3.420 €')}
                </span>
                {compact ? null : (
                    <span className={cn('text-xs font-semibold', CREAM_MUTED)}>
                        {t('routerMocks.result.caption', 'de beneficio real · 12 % de tus ventas')}
                    </span>
                )}
            </div>
            <div className={cn('flex items-center', compact ? 'gap-3' : 'gap-3.5')}>
                <Donut size={compact ? 72 : 92} inset={compact ? 14 : 18} />
                <div
                    className={cn(
                        'flex flex-1 flex-col font-semibold tabular-nums',
                        compact ? 'gap-[3px] text-[10.5px]' : 'gap-1 text-[11px]',
                    )}
                >
                    <LegendRow
                        dot={compact ? 7 : 8}
                        color='var(--tipi-flame)'
                        label={t('routerMocks.result.rawMaterials', 'Materia prima')}
                        value='31 %'
                    />
                    <LegendRow
                        dot={compact ? 7 : 8}
                        color='var(--tipi-ember)'
                        label={t('routerMocks.result.payroll', 'Nóminas')}
                        value='34 %'
                    />
                    <LegendRow
                        dot={compact ? 7 : 8}
                        color='oklch(45% 0.05 48)'
                        label={t('routerMocks.result.rent', 'Alquiler')}
                        value='10 %'
                    />
                    {compact ? (
                        <LegendRow
                            dot={7}
                            color='var(--tipi-green)'
                            label={t('routerMocks.result.profit', 'Beneficio')}
                            value='12 %'
                        />
                    ) : (
                        <>
                            <LegendRow
                                color='oklch(62% 0.04 60)'
                                label={t('routerMocks.result.utilities', 'Suministros')}
                                value='6 %'
                            />
                            <LegendRow
                                color='oklch(76% 0.03 80)'
                                label={t('routerMocks.result.fixedCosts', 'Gastos fijos')}
                                value='7 %'
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export function DishLossCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'flex flex-col border border-[oklch(62%_0.20_33/0.45)] bg-tipi-surface-up',
                compact
                    ? cn(
                          'absolute bottom-0 right-0 w-[266px] rotate-3 gap-[9px] rounded-2xl px-[13px] pb-3 pt-[11px]',
                          FLOAT_SHADOW,
                      )
                    : cn('w-[300px] translate-x-[60px] rotate-2 gap-2.5 rounded-2xl px-3.5 py-3', CARD_SHADOW),
                className,
            )}
        >
            {compact ? (
                <span className='text-[10px] font-bold uppercase tracking-[0.14em] text-tipi-flame'>
                    {t('routerMocks.dishLoss.label', 'Mejora este plato')}
                </span>
            ) : null}
            <div className='grid grid-cols-[1fr_auto] items-center gap-x-2.5 gap-y-0.5'>
                <div className='flex min-w-0 flex-col gap-[3px]'>
                    <span className={cn('font-bold', compact ? 'text-sm' : 'text-[15px] tracking-[-0.01em]')}>
                        {t('routerMocks.dishLoss.dish', 'Ensalada César')}
                    </span>
                    <span
                        className={cn(
                            'flex items-center font-semibold text-tipi-flame',
                            compact ? 'gap-1 text-[11px]' : 'gap-[5px] text-xs',
                        )}
                    >
                        <TriangleAlert size={compact ? 12 : 13} strokeWidth={2.2} />
                        {t('routerMocks.dishLoss.loss', 'Pierdes 34 € al mes')}
                    </span>
                </div>
                <div className='flex flex-col items-end gap-0.5 tabular-nums'>
                    {compact ? null : (
                        <span className='whitespace-nowrap text-[11px] font-semibold'>
                            {t('routerMocks.dishLoss.price', 'PVP 12,50 €')}
                        </span>
                    )}
                    <div className='flex items-end gap-2.5'>
                        <div className='flex flex-col items-end gap-px'>
                            <span
                                className={cn(
                                    'font-bold leading-[1.15] text-tipi-flame',
                                    compact ? 'text-xs' : 'text-[13px]',
                                )}
                            >
                                {t('routerMocks.dishLoss.foodCost', '38,0 %')}
                            </span>
                            <span className='text-[9px] font-semibold text-tipi-muted'>
                                {t('routerMocks.dishLoss.foodCostLabel', 'food cost')}
                            </span>
                        </div>
                        <span
                            className={cn(
                                'font-extrabold leading-[1.15] tracking-[-0.02em] text-tipi-flame',
                                compact ? 'text-[15px]' : 'text-base',
                            )}
                        >
                            {t('routerMocks.dishLoss.perServing', '−0,35 €')}
                        </span>
                    </div>
                </div>
            </div>
            <MarginBar greenPct={74} />
        </div>
    )
}

export function InvoicesCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    const pad = compact ? 'px-3' : 'px-3.5'
    const icon = compact ? 'size-[30px]' : 'size-9'
    return (
        <div
            className={cn(
                'flex flex-col gap-px overflow-hidden border border-tipi-border bg-tipi-border',
                CARD_SHADOW,
                compact
                    ? 'absolute left-0 top-0 w-[262px] -rotate-[4deg] rounded-[18px]'
                    : 'w-[320px] -rotate-2 rounded-[20px]',
                className,
            )}
        >
            <div
                className={cn(
                    'flex items-center justify-between gap-2.5 bg-tipi-surface',
                    pad,
                    compact ? 'py-[9px]' : 'py-3',
                )}
            >
                <span
                    className={cn(
                        'font-bold uppercase tracking-[0.14em] text-tipi-muted',
                        compact ? 'text-[10px]' : 'text-[11px]',
                    )}
                >
                    {t('routerMocks.invoices.label', 'Facturas · Junio')}
                </span>
                <span className={cn('font-bold text-tipi-muted', compact ? 'text-[10px]' : 'text-[11px]')}>
                    {t('routerMocks.invoices.summary', '1 cuadra · 1 no')}
                </span>
            </div>
            <div className={cn('flex items-center gap-3 bg-tipi-surface py-3', pad, compact && 'gap-2.5 py-2.5')}>
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded-full bg-tipi-flame-dim text-tipi-flame',
                        icon,
                    )}
                >
                    <TriangleAlert size={compact ? 15 : 17} strokeWidth={2.2} />
                </span>
                <div className='flex min-w-0 flex-1 flex-col gap-[3px]'>
                    <span className='flex items-baseline gap-1.5'>
                        <span className={cn('whitespace-nowrap font-bold', compact ? 'text-[13px]' : 'text-sm')}>
                            F-26/0620
                        </span>
                        {compact ? null : (
                            <span className='text-[11px] font-semibold text-tipi-muted'>
                                {t('routerMocks.invoices.date1', '20 jun')}
                            </span>
                        )}
                    </span>
                    <span className={cn('font-bold leading-snug text-tipi-flame', compact ? 'text-[11px]' : 'text-xs')}>
                        {compact
                            ? t('routerMocks.invoices.mismatchShort', '+278,30 € en tu contra')
                            : t('routerMocks.invoices.mismatch', 'No cuadra · +278,30 € en tu contra')}
                    </span>
                </div>
                <div className='flex flex-col items-end gap-1 tabular-nums'>
                    <span className={cn('whitespace-nowrap font-bold', compact ? 'text-[13px]' : 'text-sm')}>
                        {t('routerMocks.invoices.amount1', '2.140,80 €')}
                    </span>
                    {compact ? null : (
                        <span className='flex items-center gap-1 whitespace-nowrap text-[11px] font-bold text-tipi-flame'>
                            <span className='size-2.5 rounded-full border-[1.5px] border-dashed border-tipi-flame' />
                            {t('routerMocks.invoices.pending', 'Pendiente')}
                        </span>
                    )}
                </div>
            </div>
            <div className={cn('flex items-center gap-3 bg-tipi-surface py-3', pad, compact && 'gap-2.5 py-2.5')}>
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded-full bg-tipi-green-dim text-tipi-green',
                        icon,
                    )}
                >
                    <CircleCheck size={compact ? 15 : 17} />
                </span>
                <div className='flex min-w-0 flex-1 flex-col gap-[3px]'>
                    <span className='flex items-baseline gap-1.5'>
                        <span className={cn('whitespace-nowrap font-bold', compact ? 'text-[13px]' : 'text-sm')}>
                            F-26/0598
                        </span>
                        {compact ? null : (
                            <span className='text-[11px] font-semibold text-tipi-muted'>
                                {t('routerMocks.invoices.date2', '6 jun')}
                            </span>
                        )}
                    </span>
                    <span className={cn('font-bold text-tipi-green', compact ? 'text-[11px]' : 'text-xs')}>
                        {compact
                            ? t('routerMocks.invoices.matchPaid', 'Cuadra · Pagada')
                            : t('routerMocks.invoices.match', 'Cuadra')}
                    </span>
                </div>
                <div className='flex flex-col items-end gap-1 tabular-nums'>
                    <span className={cn('whitespace-nowrap font-bold', compact ? 'text-[13px]' : 'text-sm')}>
                        {t('routerMocks.invoices.amount2', '1.312,00 €')}
                    </span>
                    {compact ? null : (
                        <span className='flex items-center gap-1 text-[11px] font-bold text-tipi-green'>
                            <span className='flex size-3 items-center justify-center rounded-full bg-tipi-green text-tipi-bg'>
                                <Check size={8} strokeWidth={4} />
                            </span>
                            {t('routerMocks.invoices.paid', 'Pagada')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

export function RecommendationCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'flex flex-col border-[1.5px] border-[oklch(95%_0.012_85/0.85)] bg-tipi-surface-up',
                compact
                    ? cn(
                          'absolute bottom-0 right-0 w-[270px] rotate-3 gap-[9px] rounded-2xl px-3 pb-2.5 pt-3',
                          FLOAT_SHADOW,
                      )
                    : cn('w-[310px] rotate-2 gap-2.5 rounded-2xl px-3.5 pb-3 pt-3.5', CARD_SHADOW),
                className,
            )}
        >
            {compact ? null : (
                <span className='self-start rounded-full bg-tipi-flame-dim px-[9px] py-[3px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-tipi-flame'>
                    {t('routerMocks.recommendation.badge', 'Recomendada')}
                </span>
            )}
            <div className={cn('flex items-start', compact ? 'gap-2.5' : 'gap-[11px]')}>
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center bg-[oklch(100%_0_0/0.06)] text-tipi-flame',
                        compact ? 'size-8 rounded-[10px]' : 'size-9 rounded-[11px]',
                    )}
                >
                    <Scale size={compact ? 16 : 18} />
                </span>
                <div className='flex min-w-0 flex-1 flex-col gap-[5px]'>
                    <span
                        className={cn(
                            'font-extrabold leading-tight tracking-[-0.01em]',
                            compact ? 'text-sm' : 'text-[15px]',
                        )}
                    >
                        {t('routerMocks.recommendation.title', 'Reduce gramaje y sube el PVP')}
                    </span>
                    <div className='flex flex-wrap gap-1.5 tabular-nums'>
                        <span className='flex items-baseline gap-1.5 rounded-lg bg-[oklch(100%_0_0/0.07)] px-2 py-1 text-[10.5px] font-bold'>
                            {compact ? null : (
                                <span className='font-semibold text-tipi-muted'>
                                    {t('routerMocks.recommendation.portionLabel', 'Gramaje')}
                                </span>
                            )}
                            {t('routerMocks.recommendation.portion', '200 g → 180 g')}
                        </span>
                        <span className='flex items-baseline gap-1.5 rounded-lg bg-[oklch(100%_0_0/0.07)] px-2 py-1 text-[10.5px] font-bold'>
                            {compact ? null : (
                                <span className='font-semibold text-tipi-muted'>
                                    {t('routerMocks.recommendation.priceLabel', 'PVP')}
                                </span>
                            )}
                            {t('routerMocks.recommendation.price', '24 € → 26 €')}
                        </span>
                    </div>
                </div>
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded-full bg-tipi-fg text-tipi-cream-fg',
                        compact ? 'size-5' : 'size-[22px]',
                    )}
                >
                    <Check size={compact ? 12 : 13} strokeWidth={3} />
                </span>
            </div>
            <div className='flex flex-col gap-1.5 border-t border-[oklch(100%_0_0/0.07)] pt-2 tabular-nums'>
                <div className='flex items-baseline justify-between gap-2.5'>
                    <span
                        className={cn(
                            'font-extrabold leading-none tracking-[-0.02em] text-tipi-green',
                            compact ? 'text-base' : 'text-[17px]',
                        )}
                    >
                        {t('routerMocks.recommendation.gain', '+259 €/mes')}
                    </span>
                    <span className='text-[10.5px] font-semibold text-tipi-muted'>
                        {t('routerMocks.recommendation.foodCostBefore', 'FC 38,0 % →')}{' '}
                        <span className='font-bold text-tipi-fg'>
                            {t('routerMocks.recommendation.foodCostAfter', '32,4 %')}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    )
}

const SUPPLIER_ROWS = [
    { id: 'row1', status: 'incident' },
    { id: 'row2', status: 'paid' },
    { id: 'row3', status: 'unpaid' },
] as const

const SUPPLIER_DEFAULTS = {
    row1: { name: 'Makro · Pescado', meta: '3 albaranes · sin pagar', amount: '2.140,80 €' },
    row2: { name: 'Frutas Pepe', meta: '5 albaranes · pagado', amount: '684,50 €' },
    row3: { name: 'Carnes Ruiz', meta: '2 albaranes · pendiente', amount: '1.312,00 €' },
} as const

/** Supplier list of the app: month spend per supplier with delivery-note payment state and incidents. */
export function SuppliersCard({ compact, className }: MockCardProps) {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                'flex flex-col overflow-hidden border border-tipi-border bg-tipi-surface',
                compact
                    ? cn('absolute bottom-0 right-0 w-[262px] rotate-[3deg] rounded-[18px]', FLOAT_SHADOW)
                    : cn('w-[320px] rotate-2 rounded-[20px]', CARD_SHADOW),
                className,
            )}
        >
            <span
                className={cn(
                    'font-bold uppercase tracking-[0.14em] text-tipi-muted',
                    compact ? 'px-3 py-[9px] text-[10px]' : 'px-3.5 py-3 text-[11px]',
                )}
            >
                {t('routerMocks.suppliers.label', 'Proveedores')}
            </span>
            {SUPPLIER_ROWS.map(({ id, status }) => (
                <div
                    key={id}
                    className={cn(
                        'flex items-center gap-2.5 border-t border-tipi-border',
                        compact ? 'px-3 py-2.5' : 'px-3.5 py-3',
                    )}
                >
                    <div className='flex min-w-0 flex-1 flex-col gap-[3px]'>
                        <span className='flex items-center gap-1.5'>
                            <span className={cn('truncate font-bold', compact ? 'text-[13px]' : 'text-sm')}>
                                {t(`routerMocks.suppliers.${id}.name`, SUPPLIER_DEFAULTS[id].name)}
                            </span>
                            {status === 'incident' ? (
                                <TriangleAlert size={13} strokeWidth={2.2} className='shrink-0 text-tipi-flame' />
                            ) : null}
                        </span>
                        <span
                            className={cn(
                                'truncate font-semibold text-tipi-muted',
                                compact ? 'text-[11px]' : 'text-xs',
                            )}
                        >
                            {t(`routerMocks.suppliers.${id}.meta`, SUPPLIER_DEFAULTS[id].meta)}
                        </span>
                    </div>
                    <span
                        className={cn('whitespace-nowrap font-bold tabular-nums', compact ? 'text-[13px]' : 'text-sm')}
                    >
                        {t(`routerMocks.suppliers.${id}.amount`, SUPPLIER_DEFAULTS[id].amount)}
                    </span>
                    {status === 'paid' ? (
                        <span className='flex size-[18px] shrink-0 items-center justify-center rounded-md bg-tipi-green text-tipi-bg'>
                            <Check size={12} strokeWidth={3.5} />
                        </span>
                    ) : (
                        <span className='border-tipi-muted/60 size-[18px] shrink-0 rounded-full border-[1.5px]' />
                    )}
                </div>
            ))}
        </div>
    )
}

/** Floating product cards on the left and right of the quiz (desktop only). */
export function DesktopSideCards({ scale }: { scale: number }) {
    const side = 'pointer-events-none absolute inset-y-0 flex flex-col justify-center gap-[22px]'
    return (
        <div aria-hidden='true'>
            <div
                className={cn(side, 'left-[max(20px,calc(50%-680px))] origin-left')}
                style={{ transform: `scale(${scale})` }}
            >
                <PriceRiseCard />
                <DishLossCard />
                <InvoicesCard />
            </div>
            <div
                className={cn(side, 'right-[max(20px,calc(50%-680px))] origin-right items-end')}
                style={{ transform: `scale(${scale})` }}
            >
                <ResultCard />
                <WhatsAppCard />
                <RecommendationCard />
            </div>
        </div>
    )
}

/** Mobile composition: a different pair of cards for each quiz step. */
export function MobileComposition({ step, scale }: { step: 0 | 1 | 2; scale: number }) {
    return (
        <div
            aria-hidden='true'
            className='pointer-events-none relative h-[260px] w-[340px] shrink-0'
            style={{ transform: `scale(${scale})` }}
        >
            {step === 0 ? (
                <>
                    <ResultCard compact />
                    <DishLossCard compact />
                </>
            ) : null}
            {step === 1 ? (
                <>
                    <PriceRiseCard compact />
                    <WhatsAppCard compact />
                </>
            ) : null}
            {step === 2 ? (
                <>
                    <InvoicesCard compact />
                    <RecommendationCard compact />
                </>
            ) : null}
        </div>
    )
}
